import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { IContract } from "dto/Contracts/IContract";
import { IAttachment } from "dto/IAttachment";
import { ItemKind } from "dto/IKindItems";
import workflowService from "api/workflowService";
import { formatMoney } from "utils/formatMoney";
import { generateContractMetricPdf } from "utils/generateContractMetricPdf";
import usePDF from "hooks/usePDF";
import SplitSlide, { IPreviewState } from "components/SplitSlide";
import PdfPreviewPane from "components/SplitSlide/PdfPreviewPane";
import styles from "./styles.module.scss";

// Wspólne klucze proporcji/zoomu dla layoutu metryk (model 1 = SplitSlide).
const LS_SPLIT_KEY = "metric_slide_split_v1";
const LS_ZOOM_KEY = "metric_slide_zoom_v1";

interface Props {
  contract: IContract;
  onClose: () => void;
}

const fmtDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const NOTICE_LABELS: Record<string, string> = {
  Week: "Tydzień",
  TwoWeeks: "Dwa tygodnie",
  Month: "Miesiąc",
  ThreeMonths: "Trzy miesiące",
  Other: "Inny",
};

const durationLabel = (c: IContract): string =>
  c.Duration?.Type === "FixedTerm"
    ? `Do ${fmtDate(c.Duration.Until)}`
    : "Bezterminowa";

const noticeLabel = (c: IContract): string =>
  c.NoticePeriod === "Other"
    ? c.NoticePeriodDescription || "Inny"
    : NOTICE_LABELS[c.NoticePeriod] ?? "—";

const ContractMetricSlide = ({ contract, onClose }: Props) => {
  const [generating, setGenerating] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  // Workflow „contract" — definicja procedury akceptacji umów (BE).
  const template = workflowService.useActiveTemplate("contract");
  const steps = template?.steps.slice().sort((a, b) => a.order - b.order) ?? [];
  const parties = contract.Parties?.map((p) => p.Name) ?? [];

  // Podgląd umowy: plik załączony (FileUri + klucz obiektu), a gdy go brak —
  // generowana metryka jako fallback (analogicznie do metryki faktury).
  const { decryptPackage } = usePDF();
  const hasAttachedFile = !!contract.FileUri && !!contract.EncryptionKeyId;

  // ownerKind użyty do pobrania pliku (GET /file?...&ownerKind=). Brak dedykowanej
  // wartości ItemKind dla umów — do potwierdzenia/uzupełnienia po stronie BE
  // (patrz docs/backend-note-invoice-file-key.md). To część znanych problemów z
  // prezentacją pliku z bucketów, które zespół rozwiązuje we własnym zakresie.
  const FETCH_OWNER_KIND = ItemKind.None;

  // Model 1 (SplitSlide): podgląd po lewej jest ZAWSZE widoczny, więc ładujemy go
  // automatycznie po otwarciu — plik umowy, a w razie jego braku generowaną metrykę.
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        if (hasAttachedFile) {
          const attachment = {
            Id: contract.Id,
            Uri: contract.FileUri,
            FileName: contract.FileName ?? "umowa.pdf",
            EncryptionKeyId: contract.EncryptionKeyId,
            Kind: FETCH_OWNER_KIND,
            ContentType: "application/pdf",
          } as unknown as IAttachment;
          const blob = await Promise.race<Blob>([
            decryptPackage(attachment, FETCH_OWNER_KIND, false),
            new Promise<Blob>((_, reject) =>
              setTimeout(() => reject(new Error("timeout")), 20000),
            ),
          ]);
          setPdfBlob(blob);
        } else {
          const blob = await generateContractMetricPdf({
            contractNumber: contract.ContractNumber ?? "",
            signedDate: contract.SignedDate ?? "",
            parties,
            subject: contract.Subject ?? "",
            netAmount: contract.NetAmount ?? 0,
            vatAmount: contract.VatAmount ?? 0,
            grossAmount: contract.GrossAmount ?? 0,
            duration: durationLabel(contract),
            noticePeriod: noticeLabel(contract),
            workflowSteps: steps.map((s) => ({
              name: s.name,
              documentFormula: s.documentFormula,
            })),
          });
          setPdfBlob(blob);
        }
      } catch (e) {
        const reason = e instanceof Error ? e.message : String(e);
        setPdfError(
          hasAttachedFile
            ? "Nie udało się pobrać/odszyfrować pliku umowy z bucketów. Najczęstsze " +
                "przyczyny (po stronie BE): brak klucza obiektu w odpowiedzi, klucz " +
                "zawinięty dla niewłaściwego członka albo błędny ownerKind przy GET /file " +
                "(dla umów brak dedykowanej wartości ItemKind). Błąd: " +
                reason
            : "Nie udało się przygotować podglądu metryki umowy.",
        );
      } finally {
        setGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewState: IPreviewState | null = generating
    ? { text: "Ładowanie podglądu umowy…" }
    : pdfError
    ? { text: pdfError, isError: true }
    : !pdfBlob
    ? { text: "Brak podglądu umowy." }
    : null;

  const title = contract.ContractNumber
    ? `Metryka umowy — ${contract.ContractNumber}`
    : "Metryka umowy";

  return (
    <SplitSlide
      title={title}
      splitStorageKey={LS_SPLIT_KEY}
      devTodoKey="slide:contract-metric"
      onClose={onClose}
      previewState={previewState}
      preview={
        pdfBlob && (
          <PdfPreviewPane
            file={pdfBlob}
            title={hasAttachedFile ? "Załączony plik umowy" : "Metryka umowy"}
            zoomStorageKey={LS_ZOOM_KEY}
          />
        )
      }
    >
      <div className={styles.body}>
        {/* Dane umowy — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("info")}
        >
          <span>Dane umowy</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.info ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.info && (
          <div className={styles.metaCard}>
            <div className={styles.metaTitleRow}>
              <div className={styles.metaTitleLabel}>Przedmiot</div>
              <div className={styles.metaTitleValue}>
                {contract.Subject || "—"}
              </div>
            </div>
            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Numer</span>
                <span className={styles.metaValue}>
                  {contract.ContractNumber || "—"}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Zawarta</span>
                <span className={styles.metaValue}>
                  {fmtDate(contract.SignedDate)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Netto</span>
                <span className={styles.metaValue}>
                  {formatMoney(contract.NetAmount ?? 0)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>VAT</span>
                <span className={styles.metaValue}>
                  {formatMoney(contract.VatAmount ?? 0)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Brutto</span>
                <span className={styles.metaValue}>
                  {formatMoney(contract.GrossAmount ?? 0)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Wiąże</span>
                <span className={styles.metaValue}>
                  {durationLabel(contract)}
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Wypowiedzenie</span>
                <span className={styles.metaValue}>
                  {noticeLabel(contract)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Strony umowy — podmioty wpisane w BE jako Strony_umowy */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("parties")}
        >
          <span>Strony umowy</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.parties ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.parties &&
          (contract.Parties?.length ? (
            <div className={styles.partiesList}>
              {contract.Parties.map((p, i) => (
                <div key={i} className={styles.partyRow}>
                  <div className={styles.partyText}>
                    <div className={styles.partyName}>
                      <span>{p.Name || "—"}</span>
                      {p.Role && (
                        <span className={styles.partyRole}>{p.Role}</span>
                      )}
                    </div>
                    {p.IdentifierValue && (
                      <div className={styles.partyMeta}>
                        {p.IdentifierType}: {p.IdentifierValue}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noTimeline}>Brak stron umowy</div>
          ))}

        {/* Procedura akceptacji — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("procedure")}
        >
          <span>Procedura akceptacji</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.procedure ? faChevronRight : faChevronDown}
          />
        </div>

        {!collapsed.procedure &&
          (steps.length === 0 ? (
            <div className={styles.noTimeline}>
              Brak zdefiniowanej procedury akceptacji dla umów
            </div>
          ) : (
            <div className={styles.steps}>
              {steps.map((step, i) => {
                const isLast = i === steps.length - 1;
                return (
                  <div key={step.id} className={styles.step}>
                    <div className={styles.stepLine}>
                      <div
                        className={styles.stepDot}
                        style={{ background: "#5d49c7" }}
                      />
                      {!isLast && <div className={styles.stepConnector} />}
                    </div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepNumber}>Etap {i + 1}</div>
                      <div className={styles.stepName}>{step.name}</div>
                      {step.documentFormula?.trim() && (
                        <div className={styles.formulaBlock}>
                          {step.documentFormula}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </SplitSlide>
  );
};

export default ContractMetricSlide;
